<template>
  <div 
    class="group relative w-full transform transition-all duration-700 hover:scale-[1.02]"
    style="perspective: 1000px"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Glow Effect -->
    <div 
      class="absolute -inset-1 bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500 rounded-2xl opacity-0 group-hover:opacity-25 transition-all duration-700"
    ></div>

    <!-- Main Card -->
    <div class="relative bg-gradient-to-br from-zinc-800/90 via-zinc-900/95 to-zinc-800/90 rounded-2xl overflow-hidden border border-zinc-700/50 shadow-2xl">
      
      <!-- Animated Background Pattern -->
      <div class="absolute inset-0 opacity-5">
        <div class="absolute inset-0 bg-gradient-to-r from-teal-500/20 via-transparent to-emerald-500/20"></div>
        <div 
          class="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent transition-all duration-1000"
          :class="{ 'translate-x-full': isHovered }"
        ></div>
      </div>

      <!-- Header Section -->
      <div class="relative p-6 pb-4">
        <div class="flex items-center justify-between mb-6">
          <div 
            class="flex items-center space-x-3 transition-transform duration-300"
            :class="{ 'translate-x-1': isHovered }"
          >
            <div class="text-sm font-semibold text-teal-400 tracking-wider">CONQUISTA</div>
          </div>
        </div>

        <!-- Award Image with Enhanced Effects -->
        <div class="flex justify-center mb-6">
          <div 
            class="relative transition-all duration-700"
            :class="{ 
              'transform rotate-y-12 scale-105': isHovered 
            }"
            style="transform-style: preserve-3d"
          >
            <!-- Glow behind image -->
            <div 
              class="absolute -inset-6 bg-gradient-to-r from-teal-500/30 to-emerald-500/30 rounded-full blur-2xl opacity-0 transition-opacity duration-700"
              :class="{ 'opacity-100': isHovered }"
            ></div>

            <div 
              v-if="award.imageUrl" 
              class="relative w-64 h-64 sm:w-40 sm:h-40 rounded-2xl overflow-hidden"
            >
              <img 
                :src="award.imageUrl" 
                :alt="award.title" 
                class="w-full h-full object-contain transition-transform duration-700"
                :class="{ 'scale-110': isHovered }"
              />
              
              <!-- Shimmer effect -->
              <div 
                class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent z-20 transition-transform duration-1000"
                :class="{ 'translate-x-full': isHovered }"
                style="transform: translateX(-100%)"
              ></div>
            </div>

            <div 
              v-else 
              class="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center shadow-2xl border border-zinc-600/50 transition-all duration-500"
              :class="{ 'rotate-12 scale-105': isHovered }"
            >
              <span 
                class="text-5xl font-bold text-zinc-300 transition-all duration-500"
                :class="{ 'text-teal-400 scale-110': isHovered }"
              >+</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="relative px-6 pb-6 py-4">
        <!-- Enhanced gradient background -->
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-teal-500/10 to-transparent"></div>
        
        <div class="relative">
          <!-- Title and Milestone -->
          <div class="flex justify-between items-start mb-6">
            <div 
              class="transition-transform duration-300"
              :class="{ 'translate-x-1': isHovered }"
            >
              <h4 class="font-bold text-2xl text-white mb-1 tracking-tight">{{ award.title }}</h4>
              <p class="text-sm text-zinc-400 font-medium">{{ award.subtitle }}</p>
            </div>
          </div>

          <!-- Progress Section -->
          <div class="space-y-4">
            <!-- Amount Display -->
            <div class="flex justify-between items-center">
              <span 
                class="text-white font-bold text-lg transition-all duration-300"
                :class="{ 'text-teal-300 scale-105': isHovered }"
              >
                {{ formatCurrency(totalCommission) }}
              </span>
              <span class="text-zinc-500 font-medium">{{ formatCurrency(award.targetAmount) }}</span>
            </div>

            <!-- Enhanced Progress Bar -->
            <div class="relative">
              <!-- Progress track -->
              <div class="w-full bg-zinc-800 rounded-full h-4 overflow-hidden shadow-inner border border-zinc-700/50">
                <div 
                  class="h-full rounded-full bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-400 relative overflow-hidden shadow-lg transition-all duration-1000 ease-out"
                  :style="{ width: `${progressPercentage}%` }"
                >
                  <!-- Shimmer effect -->
                  <div 
                    class="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1500"
                    :class="{ 'translate-x-full animate-pulse': isHovered }"
                    style="transform: translateX(-100%)"
                  ></div>

                  <!-- Pulse effect -->
                  <div 
                    class="absolute inset-0 bg-gradient-to-r from-teal-400/50 to-emerald-400/50 transition-opacity duration-1000"
                    :class="{ 'opacity-80': isHovered }"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Status Text -->
            <div class="text-center">
              <p 
                class="text-zinc-400 text-sm font-medium transition-all duration-300"
                :class="{ 'text-zinc-300 scale-105': isHovered }"
              >
                {{ progressPercentage }}% completo
                <span class="inline-block transition-transform duration-300" :class="{ 'animate-bounce': isHovered }"></span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Accent with Animation -->
      <div 
        class="h-1 bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500 transition-all duration-1000"
        :class="{ 
          'h-2 shadow-lg shadow-teal-500/50': isHovered,
          'animate-pulse': isHovered 
        }"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

const totalCommission = ref(parseFloat(localStorage.getItem('totalCommission') || '0'))

interface Award {
  id: string
  title: string
  subtitle: string
  imageUrl?: string
  targetAmount: number
  nextMilestone: string
}

const props = defineProps<{
  award: Award
}>()

const isHovered = ref(false)

const progressPercentage = computed(() => {
  return Math.min(
    Math.round((totalCommission.value / props.award.targetAmount) * 100),
    100
  )
})

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}
</script>

<style scoped>
.rotate-y-12 {
  transform: rotateY(12deg) scale(1.05);
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 2s infinite;
}
</style>